import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, Mail, MapPin, Phone, User } from "lucide-react";
import { Button } from "@akuna/ui";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { AddressLink } from "@/components/shared/AddressLink";
import type { AppointmentDetailsController } from "@/components/calendar/useAppointmentDetailsController";

export function AppointmentPatientCard({ ctrl }: { ctrl: AppointmentDetailsController }) {
  const { t } = useTranslation("calendar");
  const {
    activePet,
    activePetSpeciesBreedAge,
    activePetId,
    appointment,
    primaryPhone,
    primaryEmail,
    ownerAddressLabel,
    ownerName,
    onOpenChange,
  } = ctrl;

  const owner = appointment?.owner;

  return (
    <section className="rounded-[18px] border border-border/30 bg-card p-4 shadow-sm">
      {/* Pet */}
      <div className="mb-3 flex items-center gap-1.5">
        <CustomIcons name="paws" className="size-4 text-muted-foreground" />
        <span className="text-sm font-bold">{t("appointmentForm.patient")}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex size-[46px] shrink-0 items-center justify-center rounded-full bg-primary/10">
          <CustomIcons name="paws" className="size-6 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold leading-tight text-primary">{activePet?.name}</div>
          {activePetSpeciesBreedAge ? (
            <div className="mt-0.5 text-xs text-muted-foreground">{activePetSpeciesBreedAge}</div>
          ) : null}
        </div>
        {activePetId ? (
          <Button
            asChild
            variant="outline"
            size="icon"
            className="size-8 shrink-0 rounded-full bg-card"
          >
            <Link
              to={`/patients/pets/${activePetId}`}
              onClick={() => onOpenChange(false)}
              aria-label={t("appointmentDetails.selectPatientToView")}
            >
              <ChevronRight className="size-[15px]" />
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="my-3.5 h-px bg-border/40" />

      {/* Owner */}
      <div className="mb-2 flex items-center gap-1.5">
        <User className="size-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground">{t("quickStart.owner")}</span>
      </div>
      {owner ? (
        <>
          {appointment?.owner_id && ownerName ? (
            <Link
              to={`/patients/owners/${appointment.owner_id}`}
              onClick={() => onOpenChange(false)}
              className="mb-2.5 block text-[15px] font-bold text-primary hover:underline"
            >
              {ownerName}
            </Link>
          ) : (
            <div className="mb-2.5 text-[15px] font-bold text-primary">{ownerName}</div>
          )}
          <div className="flex flex-col gap-2.5">
            {primaryPhone?.phone_number?.trim?.() ? (
              <div className="flex items-center gap-2.5">
                <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-emerald-500/10">
                  <Phone className="size-4 text-emerald-600" />
                </span>
                <div className="min-w-0 flex-1">
                  <span className="block text-[11px] text-muted-foreground">{t("appointmentDetails.phone")}</span>
                  {primaryPhone.is_whatsapp ? (
                    <WhatsAppPhoneLink
                      phoneNumber={primaryPhone.phone_number}
                      className="text-sm font-semibold tabular-nums"
                    />
                  ) : (
                    <span className="text-sm font-semibold tabular-nums">{primaryPhone.phone_number}</span>
                  )}
                </div>
              </div>
            ) : null}
            {primaryEmail?.email?.trim?.() ? (
              <a
                href={`mailto:${primaryEmail.email}`}
                className="flex items-center gap-2.5 text-inherit no-underline"
              >
                <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-primary/10">
                  <Mail className="size-4 text-primary" />
                </span>
                <div className="min-w-0 flex-1">
                  <span className="block text-[11px] text-muted-foreground">{t("appointmentDetails.email")}</span>
                  <span className="block truncate text-sm font-semibold">{primaryEmail.email}</span>
                </div>
              </a>
            ) : null}
            {ownerAddressLabel ? (
              <div className="flex items-start gap-2.5">
                <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-blue-500/10">
                  <MapPin className="size-4 text-blue-600" />
                </span>
                <AddressLink addressText={ownerAddressLabel} className="min-w-0 flex-1 pt-px block">
                  <span className="block text-[11px] text-muted-foreground">{t("appointmentDetails.address")}</span>
                  <span className="block text-[13px] font-medium leading-snug">{ownerAddressLabel}</span>
                </AddressLink>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <div className="text-sm text-muted-foreground">{t("appointmentDetails.noOwnerAssigned")}</div>
      )}
    </section>
  );
}
